import { FolderPlus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import type { Folder } from '../../types/api';

interface FolderActionsModalProps {
	isOpen: boolean;
	onClose: () => void;
	folder: Folder;
	action: 'createSubfolder' | 'delete';
	onConfirm: (data: any) => void;
	isLoading?: boolean;
}

/**
 * Modal for folder actions (create subfolder, delete with options)
 */
export function FolderActionsModal({
	isOpen,
	onClose,
	folder,
	action,
	onConfirm,
	isLoading = false,
}: FolderActionsModalProps) {
	const [subfolderName, setSubfolderName] = useState('');
	const [deleteOption, setDeleteOption] = useState<'move' | 'delete'>('move');
	const [confirmText, setConfirmText] = useState('');

	const handleSubmit = () => {
		if (action === 'createSubfolder') {
			if (subfolderName.trim()) {
				onConfirm({ name: subfolderName.trim() });
				setSubfolderName('');
			}
		} else if (action === 'delete') {
			if (deleteOption === 'delete' && confirmText !== 'DELETE') {
				return; // Require typing DELETE for dangerous action
			}
			onConfirm({ option: deleteOption });
			setDeleteOption('move');
			setConfirmText('');
		}
	};

	const handleClose = () => {
		setSubfolderName('');
		setDeleteOption('move');
		setConfirmText('');
		onClose();
	};

	if (!isOpen) return null;

	return (
		<div className="flex fixed inset-0 z-50 justify-center items-center">
			{/* Backdrop */}
			<div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleClose} />

			{/* Modal */}
			<div className="relative p-6 mx-4 w-full max-w-md bg-white rounded-lg shadow-xl dark:bg-gray-800">
				{action === 'createSubfolder' ? (
					<>
						<h2 className="flex gap-2 items-center mb-4 text-lg font-semibold text-gray-900 dark:text-white">
							<FolderPlus className="w-5 h-5" />
							Create Subfolder
						</h2>
						<p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
							Create a subfolder in "{folder.name}"
						</p>
						<div className="mb-4">
							<label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
								Folder Name
							</label>
							<input
								type="text"
								value={subfolderName}
								onChange={e => setSubfolderName(e.target.value)}
								placeholder="Enter folder name"
								className="px-3 py-2 w-full text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
								autoFocus
							/>
						</div>
					</>
				) : (
					<>
						<h2 className="flex gap-2 items-center mb-4 text-lg font-semibold text-gray-900 dark:text-white">
							<Trash2 className="w-5 h-5 text-red-500" />
							Delete Folder
						</h2>
						<p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
							"{folder.name}" contains {folder.flowCount} workflows and may have
							subfolders.
						</p>

						<div className="mb-4 space-y-3">
							<label className="flex gap-3 items-start">
								<input
									type="radio"
									name="deleteOption"
									value="move"
									checked={deleteOption === 'move'}
									onChange={e => setDeleteOption(e.target.value as 'move')}
									className="mt-1"
								/>
								<div>
									<div className="font-medium text-gray-900 dark:text-white">
										Move contents to parent folder
									</div>
									<div className="text-sm text-gray-600 dark:text-gray-400">
										✅ Safe option - moves all {folder.flowCount} workflows and
										subfolders to parent folder
									</div>
								</div>
							</label>

							<label className="flex gap-3 items-start">
								<input
									type="radio"
									name="deleteOption"
									value="delete"
									checked={deleteOption === 'delete'}
									onChange={e => setDeleteOption(e.target.value as 'delete')}
									className="mt-1"
								/>
								<div>
									<div className="font-medium text-red-600 dark:text-red-400">
										Delete everything (DANGEROUS)
									</div>
									<div className="text-sm text-gray-600 dark:text-gray-400">
										⚠️ Permanently deletes folder, ALL {folder.flowCount}{' '}
										workflows, and subfolders - CANNOT BE UNDONE
									</div>
								</div>
							</label>
						</div>

						{deleteOption === 'delete' && (
							<div className="p-3 mb-4 bg-red-50 rounded border border-red-200 dark:bg-red-900/20 dark:border-red-700">
								<label className="block mb-2 text-sm font-medium text-red-700 dark:text-red-300">
									Type "DELETE" to confirm permanent deletion:
								</label>
								<input
									type="text"
									value={confirmText}
									onChange={e => setConfirmText(e.target.value)}
									placeholder="DELETE"
									className="px-3 py-2 w-full text-gray-900 bg-white rounded-md border border-red-300 dark:border-red-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-red-500"
								/>
							</div>
						)}
					</>
				)}

				{/* Actions */}
				<div className="flex gap-3 justify-end">
					<button
						onClick={handleClose}
						className="px-4 py-2 text-sm font-medium text-gray-700 rounded-md transition-colors dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
						disabled={isLoading}
					>
						Cancel
					</button>
					<button
						onClick={handleSubmit}
						disabled={
							isLoading ||
							(action === 'createSubfolder' && !subfolderName.trim()) ||
							(action === 'delete' &&
								deleteOption === 'delete' &&
								confirmText !== 'DELETE')
						}
						className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
							action === 'delete' && deleteOption === 'delete'
								? 'bg-red-600 hover:bg-red-700'
								: 'bg-blue-600 hover:bg-blue-700'
						}`}
					>
						{isLoading
							? 'Processing...'
							: action === 'createSubfolder'
							? 'Create Folder'
							: 'Delete Folder'}
					</button>
				</div>
			</div>
		</div>
	);
}

export default FolderActionsModal;
